import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import i18n from '../../../i18n';
import {
  listFolder,
  uploadFiles,
  deleteFile,
  createFolder,
  deleteFolder,
  moveFile,
  moveFolder,
} from '../../../modules/utils/fleetStorage';

const VIEW_KEY = 'view-mode:fleet-documents';

/**
 * Stato + operazioni del file manager. La navigazione tra cartelle è interna
 * (array `path` di segmenti relativi al `basePath`), non sincronizzata con l'URL.
 * Espone anche vista (carte/tabella), ricerca, ordinamento e move/rename.
 */
export default function useFileManager(basePath) {
  const [path, setPath] = useState([]); // segmenti relativi a basePath
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('name'); // 'name' | 'updated' | 'size'
  const [viewMode, setViewModeState] = useState(() => localStorage.getItem(VIEW_KEY) || 'cards');

  const currentPrefix = [basePath, ...path].filter(Boolean).join('/');

  const setViewMode = useCallback((mode) => {
    localStorage.setItem(VIEW_KEY, mode);
    setViewModeState(mode);
  }, []);

  const load = useCallback(async () => {
    if (!basePath) return;
    setLoading(true);
    try {
      const { folders: f, files: fl } = await listFolder(currentPrefix);
      setFolders(f);
      setFiles(fl);
    } catch (error) {
      console.error('Errore nel caricamento documenti:', error);
    } finally {
      setLoading(false);
    }
  }, [basePath, currentPrefix]);

  useEffect(() => {
    load();
  }, [load]);

  // ===== Liste derivate: filtro ricerca + ordinamento (cartelle sempre prima) =====
  const visibleFolders = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? folders.filter((f) => f.name.toLowerCase().includes(q)) : folders;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [folders, query]);

  const visibleFiles = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? files.filter((f) => f.name.toLowerCase().includes(q)) : files;
    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sortKey === 'updated') return new Date(b.updated || 0) - new Date(a.updated || 0);
      if (sortKey === 'size') return (b.size || 0) - (a.size || 0);
      return a.name.localeCompare(b.name);
    });
    return sorted;
  }, [files, query, sortKey]);

  // ===== Navigazione =====
  const enterFolder = (name) => setPath((p) => [...p, name]);
  const goToIndex = (index) => setPath((p) => p.slice(0, index)); // numero segmenti da tenere

  // ===== Mutazioni =====
  const withBusy = async (fn, { successMsg, errorMsg } = {}) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await load();
      return true;
    } catch (error) {
      console.error(error);
      // `name_taken` è un esito prevedibile (nome già usato): merita il proprio
      // messaggio invece del generico "errore".
      if (error?.code === 'name_taken') {
        toast.error(
          i18n.t('nome_gia_esistente', {
            defaultValue: 'Esiste già una voce con questo nome in questa cartella',
          })
        );
      } else if (errorMsg) {
        toast.error(errorMsg);
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  const upload = (fileList) => {
    if (!fileList?.length) return Promise.resolve();
    return withBusy(() => uploadFiles(currentPrefix, fileList), {
      successMsg: i18n.t('documenti_caricati_successo'),
      errorMsg: i18n.t('errore_upload'),
    });
  };

  const addFolder = (name) => {
    const clean = (name || '').trim();
    if (!clean) return Promise.resolve();
    return withBusy(() => createFolder(currentPrefix, clean), {
      errorMsg: i18n.t('errore_upload'),
    });
  };

  const removeFile = (fullPath) =>
    withBusy(() => deleteFile(fullPath), {
      successMsg: i18n.t('documento_eliminato_successo'),
      errorMsg: i18n.t('errore_eliminazione'),
    });

  const removeFolder = (fullPath) =>
    withBusy(() => deleteFolder(fullPath), {
      successMsg: i18n.t('documento_eliminato_successo'),
      errorMsg: i18n.t('errore_eliminazione'),
    });

  // item = { kind: 'file' | 'folder', fullPath, name }
  const move = (item, targetPrefix) =>
    withBusy(
      () =>
        item.kind === 'folder'
          ? moveFolder(item.fullPath, targetPrefix)
          : moveFile(item.fullPath, targetPrefix),
      {
        successMsg: i18n.t('documento_spostato_successo', { defaultValue: 'Spostato' }),
        errorMsg: i18n.t('errore_spostamento', { defaultValue: 'Errore nello spostamento' }),
      }
    );

  const rename = (item, newName) => {
    const clean = (newName || '').trim();
    if (!clean || clean === item.name) return Promise.resolve();
    return withBusy(
      () =>
        item.kind === 'folder'
          ? moveFolder(item.fullPath, currentPrefix, clean)
          : moveFile(item.fullPath, currentPrefix, clean),
      {
        successMsg: i18n.t('documento_rinominato_successo', { defaultValue: 'Rinominato' }),
        errorMsg: i18n.t('errore_rinomina', { defaultValue: 'Errore nella rinomina' }),
      }
    );
  };

  return {
    path,
    currentPrefix,
    folders: visibleFolders,
    files: visibleFiles,
    loading,
    busy,
    query,
    setQuery,
    sortKey,
    setSortKey,
    viewMode,
    setViewMode,
    enterFolder,
    goToIndex,
    upload,
    addFolder,
    removeFile,
    removeFolder,
    move,
    rename,
  };
}
